import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetAgriculturalInfluencersQuery, useDeleteAgriculturalInfluencerMutation, useToggleAgriculturalInfluencerStatusMutation, useGetAdminSettingsQuery, useUpdateAdminSettingsMutation } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import DateRangePicker from '../../../components/DateRangePicker';
import ActionButtons from '../../../components/ActionButtons';
import Pagination from '../../../components/Pagination';
import ImageUpload from '../../../components/ImageUpload';
import { showSuccess, showError } from '../../../utils/toast';
import { confirmAction } from '../../../utils/confirm';

// Format date as "d M, Y" (e.g., "27 Mar, 2023")
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = date.getDate().toString().padStart(2, '0');
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month}, ${year}`;
};

const AdminAgriculturalInfluencers: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [daterange, setDaterange] = useState('');
  const [page, setPage] = useState(1);
  const { data, isLoading, error, refetch } = useGetAgriculturalInfluencersQuery({ search, daterange, page });
  const [deleteInfluencer] = useDeleteAgriculturalInfluencerMutation();
  const [toggleInfluencerStatus] = useToggleAgriculturalInfluencerStatusMutation();
  
  // Settings Logic
  const { data: settingsData } = useGetAdminSettingsQuery(undefined);
  const [updateSettings, { isLoading: isUpdatingSettings }] = useUpdateAdminSettingsMutation();
  
  const [settingsForm, setSettingsForm] = useState<{ title: string; title_ar: string; icon: File | null; existingIcon?: string }>({
    title: '',
    title_ar: '',
    icon: null,
  });

  useEffect(() => {
    if (settingsData?.data?.agricultural_influencers) {
        const agri = settingsData.data.agricultural_influencers;
        setSettingsForm(prev => ({
            ...prev,
            title: agri.title || '',
            title_ar: agri.title_ar || '',
            existingIcon: agri.icon
        }));
    }
  }, [settingsData]);

  // Reset to page 1 when search or daterange changes
  useEffect(() => {
    setPage(1);
  }, [search, daterange]);

  const handleSettingsInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSettingsForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSettingsIconChange = (file: File | null) => {
    setSettingsForm(prev => ({ ...prev, icon: file }));
  };

  const handleSettingsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const submitData = new FormData();
      // We need to construct the agricultural_influencers object
      // But since FormData can't handle nested objects easily unless we stringify or use dot notation handled by backend
      // My backend implementation expects `agricultural_influencers` as array/object and `agricultural_influencers_icon` as file.
      // Laravel validation or Request handling:
      // $request->agricultural_influencers comes from form data.
      // If I send agricultural_influencers[title], Laravel sees it as array.
      
      submitData.append('agricultural_influencers[title]', settingsForm.title);
      submitData.append('agricultural_influencers[title_ar]', settingsForm.title_ar);
      
      if (settingsForm.icon) {
        submitData.append('agricultural_influencers_icon', settingsForm.icon);
      }

      const result = await updateSettings(submitData).unwrap();
      showSuccess(result.message || 'Settings updated successfully!');
    } catch (err: any) {
        showError(err?.data?.message || 'Failed to update settings.');
    }
  };

  const handleToggleStatus = async (id: number, currentStatus: boolean) => {
    try {
      const newStatus = currentStatus ? false : true;
      const result = await toggleInfluencerStatus({ id, status: newStatus }).unwrap();
      showSuccess(result.message || 'Status updated successfully!');
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to update status. Please try again.');
    }
  };

  const handleDelete = async (id: number) => {
    const confirmed = await confirmAction(
      'Delete Influencer',
      'Are you sure you want to delete this influencer?',
      'Yes, Delete'
    );
    
    if (confirmed) {
      try {
        const result = await deleteInfluencer(id).unwrap();
        showSuccess(result.message || 'Influencer deleted successfully!');
        refetch();
      } catch (err: any) {
        showError(err?.data?.message || 'Failed to delete influencer. Please try again.');
      }
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  if (error) {
    return <div className="alert alert-danger m-5">Error loading influencers</div>;
  }

  const influencers = data?.data || { data: [], current_page: 1, last_page: 1 };

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">Agricultural Influencers</h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" className="text-muted text-hover-primary">Home</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">Agricultural Influencers</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card mb-7">
              <div className="card-header border-0 pt-6">
                <div className="card-title">
                  <h3 className="fw-bold fs-4 mb-0">Category Settings</h3>
                </div>
              </div>
              <div className="card-body pt-0">
                <form onSubmit={handleSettingsSubmit} className="row g-4 align-items-end">
                  <div className="col-md-4">
                    <label className="fs-6 fw-semibold mb-2">Title</label>
                    <input
                      type="text"
                      className="form-control form-control-solid"
                      placeholder="Enter Title"
                      name="title"
                      value={settingsForm.title}
                      onChange={handleSettingsInputChange}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="fs-6 fw-semibold mb-2">Title (Arabic)</label>
                    <input
                      type="text"
                      className="form-control form-control-solid"
                      placeholder="Enter Title in Arabic"
                      name="title_ar"
                      value={settingsForm.title_ar}
                      onChange={handleSettingsInputChange}
                    />
                  </div>
                  <div className="col-md-4">
                    <ImageUpload
                      existingImage={settingsForm.existingIcon}
                      onImageChange={handleSettingsIconChange}
                      label="Category Icon"
                      name="icon"
                    />
                  </div>
                  <div className="col-12">
                    <button type="submit" className="btn btn-base" disabled={isUpdatingSettings}>
                      <span className="indicator-label">Save Settings</span>
                      {isUpdatingSettings && (
                        <span className="indicator-progress">
                          Please wait...
                          <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            <div className="card">
              <div className="card-header border-0 pt-6">
                <div className="card-title">
                  <form onSubmit={(e) => { e.preventDefault(); }}>
                    <div className="d-flex align-items-center position-relative my-1">
                      <span className="svg-icon svg-icon-1 position-absolute ms-6">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1" transform="rotate(45 17.0365 15.1223)" fill="currentColor"></rect>
                          <path d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z" fill="currentColor"></path>
                        </svg>
                      </span>
                      <input
                        type="text"
                        className="form-control form-control-solid w-350px ps-15 me-5"
                        placeholder="Search Influencer Name.."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                      <div className="position-relative d-flex align-items-center">
                        <DateRangePicker
                          value={daterange}
                          onChange={setDaterange}
                          placeholder="Date Range"
                        />
                        {(search || daterange) && (
                          <a href="#" onClick={(e) => { e.preventDefault(); setSearch(''); setDaterange(''); }} className="btn btn-sm btn-light-primary me-3">Clear</a>
                        )}
                      </div>
                    </div>
                  </form>
                </div>
                <div className="card-toolbar">
                  <a href="#" className="btn btn-base" onClick={(e) => { e.preventDefault(); navigate('/admin/agricultural-influencers/add'); }}>
                    <span className="svg-icon svg-icon-2">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect opacity="0.5" x="11.364" y="20.364" width="16" height="2" rx="1" transform="rotate(-90 11.364 20.364)" fill="currentColor"></rect>
                        <rect x="4.36396" y="11.364" width="16" height="2" rx="1" fill="currentColor"></rect>
                      </svg>
                    </span>
                    Add New Influencer
                  </a>
                </div>
              </div>
              <div className="card-body pt-0">
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed fs-6 gy-5">
                    <thead>
                      <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                        <th>#</th>
                        <th>ICON</th>
                        <th>NAME</th>
                        <th>NAME ARABIC</th>
                        <th>ROLE</th>
                        <th>LOCATION</th>
                        <th>STATUS</th>
                        <th>CREATED DATE</th>
                        <th>ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody className="fw-semibold text-gray-600">
                      {influencers.data.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="text-center py-10">
                            <span className="text-muted">No influencers found</span>
                          </td>
                        </tr>
                      ) : (
                        influencers.data.map((influencer: any, index: number) => (
                          <tr key={influencer.id}>
                            <td>{(influencers.current_page - 1) * 15 + index + 1}</td>
                            <td>
                              {influencer.icon ? (
                                <div className="symbol symbol-50px">
                                  <img src={influencer.icon} alt={influencer.title} className="w-100" />
                                </div>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>{influencer.title || 'N/A'}</td>
                            <td>{influencer.title_ar || 'N/A'}</td>
                            <td>{influencer.role || 'N/A'}</td>
                            <td>{influencer.location || 'N/A'}</td>
                            <td>
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={influencer.status === true}
                                  onChange={() => handleToggleStatus(influencer.id, influencer.status)}
                                  style={{ cursor: 'pointer' }}
                                />
                              </div>
                            </td>
                            <td>{formatDate(influencer.created_at)}</td>
                            <td className="text-center">
                              <ActionButtons
                                onEdit={() => navigate(`/admin/agricultural-influencers/${influencer.id}/edit`)}
                                onDelete={() => handleDelete(influencer.id)}
                              />
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {influencers.data.length > 0 && (
                  <div className="card-footer border-0 pt-6">
                    <Pagination
                      currentPage={influencers.current_page}
                      lastPage={influencers.last_page}
                      total={influencers.total || 0}
                      perPage={influencers.per_page || 15}
                      onPageChange={(newPage) => setPage(newPage)}
                      showTotal={true}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAgriculturalInfluencers;
